import type { MDXComponents } from "mdx/types"
import type {
  AnchorHTMLAttributes,
  HTMLAttributes,
  ImgHTMLAttributes
} from "react"
import Link from "next/link"
import { cn } from "@/utils/cn"

/**
 * Default MDX renderer used by every imported `.mdx` file in this project.
 * Tailored to the gray-950 dark theme so blog content looks great without
 * wrapping in `prose`. Posts can still override any element by passing their
 * own components map.
 */
const defaultComponents: MDXComponents = {
  h1: (props: HTMLAttributes<HTMLHeadingElement>) => (
    <h1
      className="mt-12 scroll-mt-28 text-3xl font-bold tracking-tight text-gray-50 sm:text-4xl"
      {...props}
    />
  ),
  h2: (props: HTMLAttributes<HTMLHeadingElement>) => (
    <h2
      className="mt-12 scroll-mt-28 text-2xl font-semibold tracking-tight text-gray-50 sm:text-3xl"
      {...props}
    />
  ),
  h3: (props: HTMLAttributes<HTMLHeadingElement>) => (
    <h3
      className="mt-10 scroll-mt-28 text-xl font-semibold tracking-tight text-gray-100"
      {...props}
    />
  ),
  h4: (props: HTMLAttributes<HTMLHeadingElement>) => (
    <h4
      className="mt-8 scroll-mt-28 text-lg font-semibold text-gray-100"
      {...props}
    />
  ),
  p: (props: HTMLAttributes<HTMLParagraphElement>) => (
    <p className="mt-5 text-base leading-7 text-gray-300" {...props} />
  ),
  a: ({ href, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) => {
    const target = typeof href === "string" ? href : "#"
    const isExternal = /^https?:\/\//i.test(target)
    if (isExternal) {
      return (
        <a
          href={target}
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-400 underline decoration-emerald-500/30 underline-offset-4 transition-colors hover:text-emerald-300 hover:decoration-emerald-300/60"
          {...rest}
        />
      )
    }
    return (
      <Link
        href={target}
        className="text-emerald-400 underline decoration-emerald-500/30 underline-offset-4 transition-colors hover:text-emerald-300 hover:decoration-emerald-300/60"
        {...rest}
      />
    )
  },
  ul: (props: HTMLAttributes<HTMLUListElement>) => (
    <ul
      className="mt-5 list-disc space-y-2 pl-6 text-gray-300 marker:text-emerald-400/70"
      {...props}
    />
  ),
  ol: (props: HTMLAttributes<HTMLOListElement>) => (
    <ol
      className="mt-5 list-decimal space-y-2 pl-6 text-gray-300 marker:text-emerald-400/70"
      {...props}
    />
  ),
  li: (props: HTMLAttributes<HTMLLIElement>) => (
    <li className="leading-7" {...props} />
  ),
  strong: (props: HTMLAttributes<HTMLElement>) => (
    <strong className="font-semibold text-gray-100" {...props} />
  ),
  em: (props: HTMLAttributes<HTMLElement>) => (
    <em className="italic text-gray-200" {...props} />
  ),
  blockquote: (props: HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote
      className="mt-6 border-l-4 border-emerald-500/50 bg-emerald-500/5 px-5 py-3 italic text-gray-300"
      {...props}
    />
  ),
  hr: (props: HTMLAttributes<HTMLHRElement>) => (
    <hr className="my-10 border-gray-800" {...props} />
  ),
  code: (props: HTMLAttributes<HTMLElement>) => (
    <code
      className="rounded bg-gray-900 px-1.5 py-0.5 font-mono text-[0.9em] text-emerald-300"
      {...props}
    />
  ),
  pre: ({ className, style, children, ...rest }: HTMLAttributes<HTMLPreElement>) => (
    <pre
      {...rest}
      style={style}
      className={cn(
        "group mt-6 overflow-x-auto rounded-xl border border-emerald-500/25 bg-[#0d1117] p-4 text-[0.8125rem] leading-relaxed shadow-lg shadow-black/40 ring-1 ring-black/60 [&>code]:m-0 [&>code]:block [&>code]:bg-transparent [&>code]:p-0 [&>code]:text-[0.8125rem]",
        className
      )}
    >
      {children}
    </pre>
  ),
  table: (props: HTMLAttributes<HTMLTableElement>) => (
    <div className="mt-6 overflow-x-auto rounded-xl border border-gray-800">
      <table className="w-full text-left text-sm" {...props} />
    </div>
  ),
  thead: (props: HTMLAttributes<HTMLTableSectionElement>) => (
    <thead className="border-b border-gray-800 bg-gray-900/60" {...props} />
  ),
  th: (props: HTMLAttributes<HTMLTableCellElement>) => (
    <th
      className="px-4 py-3 font-medium text-gray-300"
      {...props}
    />
  ),
  td: (props: HTMLAttributes<HTMLTableCellElement>) => (
    <td className="border-t border-gray-800/60 px-4 py-3 text-gray-300" {...props} />
  ),
  /** Inline markdown images intentionally hidden — use `meta.ogImage` for share/preview imagery */
  img: (_props: ImgHTMLAttributes<HTMLImageElement>) => null
}

export function useMDXComponents(
  components: MDXComponents = {}
): MDXComponents {
  return { ...defaultComponents, ...components }
}
